import { isListenerProfileSetupPath } from './listenerUserType';
import { parse, stringify } from './urlHelpers';

/**
 * In-page Book a time button on talksession ListingPage. The mobile sticky
 * CTA watches this id so only one pink button is visible at a time.
 */
export const IN_PAGE_BOOK_CTA_ID = 'listing-book-a-time-cta';

/** In-page Start now / Book a time panel. Hidden at desktop; sticky watches it. */
export const IN_PAGE_BOOK_PANEL_ID = 'listing-paid-booking';

export const START_NOW_SEARCH = 'startNow=1';
export const BOOK_A_TIME_SEARCH = 'book=1';
export const BOOK_FROM_CARD_STATE = 'bookFromCard';
export const BOOK_PICKER_CLOSED_STATE = 'bookPickerClosed';
export const BOOK_A_TIME_SKIP_AUTOOPEN_PREFIX = 'SweetListenersBookATimeSkipAutoOpen:';
export const BOOKING_MODE_NOW = 'now';
export const BOOKING_MODE_LATER = 'later';

const LISTING_BOOK_SUFFIX = /\/book\/?$/;

/**
 * Listing id from a published listing / checkout / book path.
 *
 * @param {string} [pathname]
 * @returns {string|null}
 */
export const listingIdFromPath = pathname => {
  if (typeof pathname !== 'string' || !pathname.startsWith('/l/')) {
    return null;
  }
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== 'l' || parts.length < 3) {
    return null;
  }
  if (['new', 'book', 'checkout'].includes(parts[2])) {
    return null;
  }
  return parts[2];
};

const canUseSessionStorage = () =>
  typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

/**
 * True when this tab already opened or dismissed Book a time for the listing.
 * Survives a remount so browser Back cannot auto-open the picker again.
 *
 * @param {string} [listingId]
 * @returns {boolean}
 */
export const hasBookATimeSkipAutoOpen = listingId => {
  if (!listingId || !canUseSessionStorage()) {
    return false;
  }
  try {
    return window.sessionStorage.getItem(`${BOOK_A_TIME_SKIP_AUTOOPEN_PREFIX}${listingId}`) === '1';
  } catch (e) {
    return false;
  }
};

/**
 * Remember that Book a time already opened or was dismissed on this listing.
 *
 * @param {string} [listingId]
 */
export const markBookATimeSkipAutoOpen = listingId => {
  if (!listingId || !canUseSessionStorage()) {
    return;
  }
  try {
    window.sessionStorage.setItem(`${BOOK_A_TIME_SKIP_AUTOOPEN_PREFIX}${listingId}`, '1');
  } catch (e) {
    // private mode — in-memory dismissedRef still covers the same instance
  }
};

/**
 * @param {string} [listingId]
 */
export const clearBookATimeSkipAutoOpen = listingId => {
  if (!listingId || !canUseSessionStorage()) {
    return;
  }
  try {
    window.sessionStorage.removeItem(`${BOOK_A_TIME_SKIP_AUTOOPEN_PREFIX}${listingId}`);
  } catch (e) {
    // ignore
  }
};

/**
 * True when this path is a published listing profile (not checkout/edit/book).
 *
 * @param {string} [pathname]
 * @returns {boolean}
 */
export const isListingProfilePath = pathname => {
  if (typeof pathname !== 'string' || !pathname.startsWith('/l/')) {
    return false;
  }
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== 'l' || parts.length < 2 || parts.length > 3) {
    return false;
  }
  const last = parts[parts.length - 1];
  return !['book', 'checkout', 'make-offer', 'request-quote', 'new'].includes(last);
};

/**
 * Profile path for a booking URL, or the path unchanged.
 *
 * @param {string} [pathname]
 * @returns {string}
 */
export const listingProfilePathFromBooking = pathname =>
  typeof pathname === 'string' ? pathname.replace(LISTING_BOOK_SUFFIX, '') : pathname;

/**
 * Booking page path for a listing profile, or the path unchanged.
 *
 * @param {string} [pathname]
 * @returns {string}
 */
export const listingBookingPathFromProfile = pathname => {
  if (typeof pathname !== 'string') {
    return pathname;
  }
  if (LISTING_BOOK_SUFFIX.test(pathname)) {
    return pathname.replace(/\/?$/, '');
  }
  if (isListingProfilePath(pathname)) {
    return `${pathname.replace(/\/$/, '')}/book`;
  }
  return pathname;
};

/**
 * now | later from the booking URL. Legacy startNow / book still map.
 *
 * @param {Object} [searchParams]
 * @returns {'now'|'later'|null}
 */
export const listingBookingMode = (searchParams = {}) => {
  const mode = searchParams.mode;
  if (mode === BOOKING_MODE_NOW) {
    return BOOKING_MODE_NOW;
  }
  if (mode === BOOKING_MODE_LATER) {
    return BOOKING_MODE_LATER;
  }
  if (isStartNowSearchParam(searchParams.startNow)) {
    return BOOKING_MODE_NOW;
  }
  if (
    isBookATimeSearchParam(searchParams.book) ||
    searchParams.bookLater === true ||
    searchParams.bookLater === 'true' ||
    searchParams.bookLater === 1 ||
    searchParams.bookLater === '1'
  ) {
    return BOOKING_MODE_LATER;
  }
  return null;
};

/**
 * True when the listing URL asked for Start now (skip the calendar).
 *
 * @param {string|boolean|number} [value]
 * @returns {boolean}
 */
export const isStartNowSearchParam = value =>
  value === true || value === 'true' || value === '1' || value === 1 || value === 'only';

/**
 * True when the listing URL asked to open Book a time immediately.
 *
 * @param {string|boolean|number} [value]
 * @returns {boolean}
 */
export const isBookATimeSearchParam = value =>
  value === true || value === 'true' || value === '1' || value === 1;

/**
 * Open the scheduled Book a time sheet (mobile) / calendar (desktop).
 *
 * @param {Object} history
 * @param {Object} [location]
 */
export const openBookATime = (history, location) => {
  const { pathname, search, state } = location || {};
  try {
    require('./bookingFunnel').trackBookingStep('book_time_tap', { once: true });
  } catch (e) {
    // ignore
  }
  if (LISTING_BOOK_SUFFIX.test(pathname || '')) {
    const parsed = parse(search);
    if (listingBookingMode(parsed) === BOOKING_MODE_LATER) {
      return;
    }
    const {
      startNow: _omitStartNow,
      orderOpen: _omitOpen,
      bookLater: _omitLater,
      book: _omitBook,
      ...rest
    } = parsed;
    history.push(`${pathname}?${stringify({ ...rest, mode: BOOKING_MODE_LATER })}`, state);
    return;
  }
  const bookingPath = listingBookingPathFromProfile(pathname);
  if (bookingPath && bookingPath !== pathname) {
    history.push(`${bookingPath}?mode=${BOOKING_MODE_LATER}`, state);
    return;
  }
  const parsed = parse(search);
  const { startNow: _omitStartNow, orderOpen: _omitOpen, bookLater: _omitLater, ...rest } = parsed;
  const searchString = stringify({ ...rest, book: 1 });
  history.push(`${pathname}?${searchString}`, state);
};

/**
 * Open the Start now panel so the talker can pick Text / Voice / Video.
 * Does not start checkout — format is required first.
 *
 * @param {Object} history
 * @param {Object} [location]
 */
export const openStartNow = (history, location) => {
  const { pathname, search, state } = location || {};
  try {
    require('./bookingFunnel').trackBookingStep('start_now_tap', { once: true });
  } catch (e) {
    // ignore
  }
  if (LISTING_BOOK_SUFFIX.test(pathname || '')) {
    const parsed = parse(search);
    if (listingBookingMode(parsed) === BOOKING_MODE_NOW) {
      return;
    }
    const { bookLater: _omitBookLater, book: _omitBook, ...rest } = parsed;
    history.push(`${pathname}?${stringify({ ...rest, mode: BOOKING_MODE_NOW })}`, state);
    return;
  }
  const bookingPath = listingBookingPathFromProfile(pathname);
  if (bookingPath && bookingPath !== pathname) {
    history.push(`${bookingPath}?mode=${BOOKING_MODE_NOW}`, state);
    return;
  }
  const parsed = parse(search);
  const { bookLater: _omitBookLater, book: _omitBook, ...rest } = parsed;
  const searchString = stringify({ ...rest, orderOpen: true, startNow: 1 });
  history.push(`${pathname}?${searchString}`, state);
};

/**
 * ListingPage search that opens Start now, not Book a time.
 *
 * @returns {{ search: string }}
 */
export const listingStartNowTo = () => ({ search: `?mode=${BOOKING_MODE_NOW}` });

/**
 * ListingPage search that opens the Book a time sheet on mount.
 *
 * @returns {{ search: string }}
 */
export const listingBookATimeTo = () => ({ search: `?mode=${BOOKING_MODE_LATER}` });

/**
 * Book Next Available — scheduled booking with the earliest open slot hinted.
 *
 * @param {number|string|null} [nextAvailableAt]
 * @returns {{ search: string }}
 */
export const listingBookNextTo = (nextAvailableAt = null) => {
  const next = Number(nextAvailableAt);
  const search = stringify({
    mode: BOOKING_MODE_LATER,
    ...(Number.isFinite(next) && next > 0 ? { next } : {}),
  });
  return { search: search ? `?${search}` : `?mode=${BOOKING_MODE_LATER}` };
};

/**
 * Search-card / featured-card destination: Start now when the listener is
 * Available now, otherwise open Book a time immediately. Marks the navigation
 * so Close / Back can return to search instead of trapping the talker in the
 * auto-opened picker.
 *
 * @param {Object} [options]
 * @param {boolean} [options.online]
 * @returns {{ search: string, state: { bookFromCard: true } }}
 */
export const listingCardTo = ({ online } = {}) => {
  const to = online ? listingStartNowTo() : listingBookATimeTo();
  return { ...to, state: { [BOOK_FROM_CARD_STATE]: true } };
};

/**
 * Choose [name] opens the booking page without forcing Talk now or later.
 * The panel shows Talk Now only when the listener is actually available.
 *
 * @returns {{ search: string, state: { bookFromCard: true } }}
 */
export const listingChooseTo = () => ({
  search: '',
  state: { [BOOK_FROM_CARD_STATE]: true },
});

/**
 * NamedLink props for Start now / Book a time — booking page, not profile.
 *
 * @param {Object} [options]
 * @param {string} options.id
 * @param {string} options.slug
 * @param {boolean} [options.online]
 * @param {'now'|'later'} [options.mode]
 * @returns {{ name: string, params: { id: string, slug: string }, to: { search: string, state: { bookFromCard: true } } }}
 */
export const listingBookingLinkProps = ({
  id,
  slug,
  online,
  mode,
  choose,
  nextAvailableAt,
} = {}) => {
  const to = choose
    ? listingChooseTo()
    : mode === 'next'
    ? { ...listingBookNextTo(nextAvailableAt), state: { [BOOK_FROM_CARD_STATE]: true } }
    : listingCardTo({
        online: mode ? mode === BOOKING_MODE_NOW : online,
      });
  return {
    name: 'ListingBookingPage',
    params: { id, slug },
    to,
  };
};

/**
 * True when this listing visit came from a search / featured card tap.
 *
 * @param {Object} [state]
 * @returns {boolean}
 */
export const isBookFromCard = (state = {}) => !!state?.[BOOK_FROM_CARD_STATE];

/**
 * True when Close or Back already dismissed the Book a time picker.
 *
 * @param {Object} [state]
 * @returns {boolean}
 */
export const isBookPickerClosed = (state = {}) => !!state?.[BOOK_PICKER_CLOSED_STATE];

/**
 * Close the booking page / Book a time sheet. From a search card, go back
 * to search. From the listing, replace onto the profile with a dismissed
 * flag so auto-open cannot push /book again.
 *
 * @param {Object} history
 * @param {Object} [location]
 */
export const closeBookATimePicker = (history, location) => {
  const { pathname, search, state } = location || {};
  markBookATimeSkipAutoOpen(listingIdFromPath(pathname));
  if (isBookFromCard(state) && history && typeof history.go === 'function') {
    history.go(-1);
    return;
  }
  const nextState = { ...(state || {}), [BOOK_PICKER_CLOSED_STATE]: true };
  delete nextState[BOOK_FROM_CARD_STATE];
  if (LISTING_BOOK_SUFFIX.test(pathname || '')) {
    const profilePath = listingProfilePathFromBooking(pathname);
    const nextPath = profilePath || '/';
    if (history && typeof history.replace === 'function') {
      history.replace(nextPath, nextState);
      return;
    }
    if (history && typeof history.push === 'function') {
      history.push(nextPath, nextState);
    }
    return;
  }
  const parsed = parse(search);
  const {
    orderOpen: _omitOpen,
    book: _omitBook,
    bookLater: _omitLater,
    mode: _omitMode,
    ...searchParams
  } = parsed;
  const searchString = stringify(searchParams);
  const nextPath = searchString ? `${pathname}?${searchString}` : pathname;
  if (history && typeof history.replace === 'function') {
    history.replace(nextPath, nextState);
    return;
  }
  if (history && typeof history.push === 'function') {
    history.push(nextPath, nextState);
  }
};

/**
 * Signed-in talkers skip the intermediate Book a time teaser. Guests keep
 * Sign up. Start now listings never auto-open the calendar.
 *
 * @param {Object} [options]
 * @param {boolean} [options.isGuest]
 * @param {boolean} [options.isOwnListing]
 * @param {boolean} [options.showStartNow]
 * @param {boolean} [options.bookFromUrl]
 * @param {boolean} [options.closedPicker]
 * @param {boolean} [options.bookPickerClosed]
 * @param {boolean} [options.skipAutoOpen]
 * @param {boolean} [options.payoutsBlocked]
 * @returns {boolean}
 */
export const shouldAutoOpenBookATime = ({
  isGuest = false,
  isOwnListing = false,
  showStartNow = false,
  bookFromUrl = false,
  closedPicker = false,
  bookPickerClosed = false,
  skipAutoOpen = false,
  payoutsBlocked = false,
} = {}) =>
  !isGuest &&
  !isOwnListing &&
  !showStartNow &&
  !bookFromUrl &&
  !closedPicker &&
  !bookPickerClosed &&
  !skipAutoOpen &&
  !payoutsBlocked;

/**
 * Whether this listing URL should show the Book a time calendar / sheet.
 * `book=1` is the card deep-link. Older `orderOpen` / `bookLater` links still work.
 *
 * @param {Object} [searchParams]
 * @returns {boolean}
 */
export const wantsBookATimeSheet = (searchParams = {}) => {
  const mode = listingBookingMode(searchParams);
  if (mode === BOOKING_MODE_LATER) {
    return true;
  }
  if (mode === BOOKING_MODE_NOW) {
    return false;
  }
  if (isBookATimeSearchParam(searchParams.book)) {
    return true;
  }
  if (isStartNowSearchParam(searchParams.startNow)) {
    return false;
  }
  return (
    searchParams.orderOpen === true ||
    searchParams.orderOpen === 'true' ||
    searchParams.bookLater === true ||
    searchParams.bookLater === 'true' ||
    searchParams.bookLater === 1 ||
    searchParams.bookLater === '1'
  );
};

/**
 * Persist a post-auth / post-verify return path for booking.
 *
 * Email verification links open `/verify-email?t=` in a new tab, so
 * react-router `location.state.from` is lost. sessionStorage is also
 * tab-scoped. localStorage is shared across tabs for the same origin.
 */

const RETURN_PATH_KEY = 'SweetListenersBookingReturnPath';
export const CHECKOUT_REAUTH_KEY = 'SweetListenersCheckoutReauth';
const CHECKOUT_DRAFT_KEY = 'CheckoutPage';
const RETURN_PATH_TTL_MS = 24 * 60 * 60 * 1000;

const canUseLocalStorage = () =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

/**
 * True for in-app listing or checkout paths we may resume after verify/login.
 *
 * @param {string} path
 * @returns {boolean}
 */
export const isBookingReturnPath = path => {
  if (typeof path !== 'string') {
    return false;
  }
  if (!path.startsWith('/') || path.startsWith('//')) {
    return false;
  }
  if (isListenerProfileSetupPath(path)) {
    return false;
  }
  return path.includes('/checkout') || path.startsWith('/l/');
};

/**
 * Remember where to send the user after email verification or login.
 *
 * @param {string} path relative path including search, e.g. /l/slug/id/checkout
 */
export const saveBookingReturnPath = path => {
  if (!canUseLocalStorage() || !isBookingReturnPath(path)) {
    return;
  }
  try {
    window.localStorage.setItem(RETURN_PATH_KEY, JSON.stringify({ path, savedAt: Date.now() }));
  } catch (e) {
    // Quota / private mode — booking still works in the same tab via sessionStorage.
  }
};

/**
 * @returns {string|null} stored relative path, or null if missing/expired
 */
export const peekBookingReturnPath = () => {
  if (!canUseLocalStorage()) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(RETURN_PATH_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed?.path || !isBookingReturnPath(parsed.path)) {
      return null;
    }
    if (parsed.savedAt && Date.now() - parsed.savedAt > RETURN_PATH_TTL_MS) {
      window.localStorage.removeItem(RETURN_PATH_KEY);
      return null;
    }
    return parsed.path;
  } catch (e) {
    return null;
  }
};

/**
 * Drop the stored return path (after a successful resume, or when stale).
 */
export const clearBookingReturnPath = () => {
  if (!canUseLocalStorage()) {
    return;
  }
  try {
    window.localStorage.removeItem(RETURN_PATH_KEY);
  } catch (e) {
    // ignore
  }
};

/**
 * Drop an abandoned Start now / Pay draft so Home and Cancel cannot resume it.
 * In-flight unpaid bookings are recovered from the API on the next Start now (Inbox).
 */
export const abandonCheckoutDraft = () => {
  clearBookingReturnPath();
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage?.removeItem(CHECKOUT_DRAFT_KEY);
  } catch (e) {
    // ignore
  }
  try {
    window.localStorage?.removeItem(CHECKOUT_DRAFT_KEY);
  } catch (e) {
    // ignore
  }
  try {
    window.sessionStorage?.removeItem(CHECKOUT_REAUTH_KEY);
  } catch (e) {
    // ignore
  }
};

/**
 * Pasting `/` or landing on Home must never reopen Pay.
 *
 * @param {string} [pathname]
 */
export const abandonCheckoutDraftIfHome = pathname => {
  const path = pathname || (typeof window !== 'undefined' ? window.location.pathname : '');
  if (path === '/' || path === '') {
    abandonCheckoutDraft();
  }
};

/**
 * Guest Book a time / Start now / Request to book → Sign up (never Login),
 * then back to this listing or checkout.
 *
 * @param {Object} history
 * @param {Object} [location]
 */
export const redirectGuestToSignupToBook = (history, location) => {
  const from = `${location?.pathname || ''}${location?.search || ''}${location?.hash || ''}`;
  const safeFrom = isBookingReturnPath(from) ? from : '/';
  if (isBookingReturnPath(from)) {
    saveBookingReturnPath(from);
    saveBookResume(from);
  }
  if (history && typeof history.push === 'function') {
    history.push('/signup', { from: safeFrom });
  }
};

// Sam t5207u Oct 28 (Live): logged-out FREE Talk Now goes to guest /talk-free
// (email + code + 18+), never full /signup. Paid / Book for later keep redirectGuestToSignupToBook.
export const redirectGuestToTalkFree = (history, location, fromOverride) => {
  const from = fromOverride || `${location?.pathname || ''}${location?.search || ''}${location?.hash || ''}`;
  const ok = isBookingReturnPath(from);
  if (ok) {
    saveBookingReturnPath(from);
    saveBookResume(from);
  }
  if (history && typeof history.push === 'function') {
    history.push('/talk-free', { from: ok ? from : '/' });
  }
};

// Sam 9:29 PM Oct 23 (TEST): a guest who tapped Talk Now / Book and signed up goes
// straight back to that booking step, never into /welcome or earner setup.
const BOOK_RESUME_KEY = 'sl_book_resume';
export const saveBookResume = path => {
  try {
    if (typeof window !== 'undefined' && isBookingReturnPath(path)) {
      window.localStorage.setItem(BOOK_RESUME_KEY, JSON.stringify({ path, at: Date.now() }));
    }
  } catch (e) {}
};
export const peekBookResume = () => {
  try {
    if (typeof window === 'undefined') return null;
    const r = JSON.parse(window.localStorage.getItem(BOOK_RESUME_KEY) || 'null');
    if (r?.path && Date.now() - r.at < 3 * 60 * 60 * 1000 && isBookingReturnPath(r.path)) return r.path;
  } catch (e) {}
  return null;
};
export const clearBookResume = () => {
  try {
    window.localStorage.removeItem(BOOK_RESUME_KEY);
  } catch (e) {}
};
